import { useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { useMutation } from "@tanstack/react-query";
import {
    Row,
    Col,
    Label,
    Button,
    Alert,
    Progress,
    FormGroup,
    Input,
    Card,
} from "reactstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ReportIncidenService from "src/modules/data-protection/pages/admin/data-subject-request/components/api/ReportIncidenService";

const REQUESTER_TYPES = {
    COMPANY: "company",
    PRIVATE_PERSON: "private_person",
};

const EMPTY_FORM = {
    requesterType: REQUESTER_TYPES.COMPANY,
    organizationName: "",
    organizationWebsite: "",
    phone: "",
    email: ""
};

const formatFileSize = (bytes = 0) => {
    if (bytes === 0) {
        return "0 Byte";
    }

    const units = ["Bytes", "KB", "MB", "GB"];
    const unitIndex = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        units.length - 1
    );

    return `${Math.round(bytes / Math.pow(1024, unitIndex))} ${units[unitIndex]}`;
};

const buildInitialForm = (initialValues, user) => {
    const savedValues = initialValues || {};
    const hasSavedValues = !!initialValues;

    return {
        requesterType: savedValues.requester_type || EMPTY_FORM.requesterType,
        organizationName: savedValues.organization_name || "",
        organizationWebsite: savedValues.organization_website || "",
        phone: hasSavedValues
            ? (savedValues.phone || "")
            : (user?.phone_number || ""),
        email: hasSavedValues
            ? (savedValues.email || "")
            : (user?.email || "")
    };
};

const DomainMismatchProof = ({
    t,
    isUploading,
    proofFile,
    fileInputRef,
    onChangeEmail,
    onFileChange,
    onRemoveProof,
}) => (
    <Alert color="warning" className="mt-2 mb-0">
        <p className="mb-2">
            {t("Email domain does not match the organization website.")}
        </p>
        <p className="mb-2">
            {t("Please change your email to a matching domain, or optionally upload proof that this email belongs to the company.")}
        </p>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <Button
                color="warning"
                outline
                size="sm"
                type="button"
                onClick={onChangeEmail}
            >
                {t("Change email")}
            </Button>
        </div>

        <div>
            <Label className="form-label mb-1" htmlFor="email_domain_proof">
                {`${t("Proof that this email belongs to the company")} ${t("(Optional)")}`}
            </Label>
            <input
                type="file"
                name="email_domain_proof"
                id="email_domain_proof"
                className="form-control fileUpload"
                ref={fileInputRef}
                onChange={onFileChange}
                disabled={isUploading}
            />

            <Progress
                color="info"
                striped
                animated
                value={100}
                hidden={!isUploading}
                className="mt-2"
            >
                {t("Please wait while the file is uploaded")}
            </Progress>

            {proofFile && !isUploading && (
                <div className="dropzone-previews mt-3" id="email-domain-proof-previews">
                    <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                        <div className="p-2">
                            <Row className="align-items-center">
                                <Col>
                                    <Link to="#" className="text-muted fw-bold">
                                        {proofFile.name}
                                    </Link>
                                    <p className="mb-0">
                                        <strong>{formatFileSize(proofFile.size)}</strong>
                                    </p>
                                </Col>
                                <Col
                                    xs={2}
                                    style={{
                                        textAlign: "right",
                                    }}
                                >
                                    <Button
                                        color="danger"
                                        size="sm"
                                        type="button"
                                        onClick={onRemoveProof}
                                    >
                                        X
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    </Alert>
);

const RequesterDetails = ({
    t,
    user = null,
    initialValues = null,
    onBackButtonClicked,
    onSubmit,
}) => {
    const proofFileInputRef = useRef(null);
    const domainCheckRequestIdRef = useRef(0);

    const [form, setForm] = useState(() => buildInitialForm(initialValues, user));
    const [emailDomainMismatch, setEmailDomainMismatch] = useState(
        () => !!initialValues?.email_domain_mismatch
    );
    const [proofAttachmentId, setProofAttachmentId] = useState(
        () => initialValues?.email_domain_proof || null
    );
    const [proofFileMeta, setProofFileMeta] = useState(null);

    const isCompanyRequester = form.requesterType === REQUESTER_TYPES.COMPANY;

    const updateForm = (patch) => {
        setForm((current) => ({ ...current, ...patch }));
    };

    const resetProofInput = () => {
        if (proofFileInputRef.current) {
            proofFileInputRef.current.value = "";
        }
    };

    const {
        mutate: checkDomainMutation,
        isLoading: isCheckingDomain,
    } = useMutation({
        mutationKey: ["admin-dsr-check-email-org-domain"],
        mutationFn: async (payload) => {
            const service = ReportIncidenService.getInstance();
            return await service.checkEmailOrgDomain(payload);
        },
        onError: () => {
            toast(t("An error occurred while checking email domain."), {
                type: "error",
            });
        },
    });

    const {
        mutate: uploadProofMutation,
        isLoading: isUploadingProof,
    } = useMutation({
        mutationKey: ["admin-dsr-upload-email-domain-proof"],
        mutationFn: async (file) => {
            const service = ReportIncidenService.getInstance();
            return await service.uploadEmailDomainProof(file);
        },
        onSuccess: (data) => {
            setProofAttachmentId(data?.id || null);
        },
        onError: () => {
            setProofAttachmentId(null);
            setProofFileMeta(null);
            resetProofInput();
            toast(t("An error occurred while uploading the file."), {
                type: "error",
            });
        },
    });

    const {
        mutate: deleteProofMutation,
    } = useMutation({
        mutationKey: ["admin-dsr-delete-email-domain-proof"],
        mutationFn: async (attachmentId) => {
            const service = ReportIncidenService.getInstance();
            return await service.deleteEmailDomainProof(attachmentId);
        },
        onError: () => {
            toast(t("An error occurred while deleting the file."), {
                type: "error",
            });
        },
    });

    const clearLocalProof = () => {
        setProofAttachmentId(null);
        setProofFileMeta(null);
        resetProofInput();
    };

    const removeProof = () => {
        if (proofAttachmentId) {
            deleteProofMutation(proofAttachmentId);
        }
        clearLocalProof();
    };

    const resetDomainMismatch = () => {
        setEmailDomainMismatch(false);
        clearLocalProof();
    };

    const runDomainCheck = ({
        email = form.email,
        organizationWebsite = form.organizationWebsite,
    } = {}) => {
        if (!isCompanyRequester || !email || !organizationWebsite) {
            resetDomainMismatch();
            return;
        }

        const requestId = ++domainCheckRequestIdRef.current;

        checkDomainMutation(
            {
                email,
                organization_website: organizationWebsite,
            },
            {
                onSuccess: (result) => {
                    if (requestId !== domainCheckRequestIdRef.current) {
                        return;
                    }

                    const isMismatch = result?.match === false;
                    setEmailDomainMismatch(isMismatch);

                    if (!isMismatch) {
                        removeProof();
                    }
                },
            }
        );
    };

    const handleRequesterTypeChange = (nextType) => {
        updateForm({
            requesterType: nextType,
            ...(nextType === REQUESTER_TYPES.PRIVATE_PERSON
                ? { organizationName: "", organizationWebsite: "" }
                : {}),
        });

        if (nextType === REQUESTER_TYPES.PRIVATE_PERSON) {
            setEmailDomainMismatch(false);
            removeProof();
        }
    };

    const handleEmailOrWebsiteChange = (field, value) => {
        updateForm({ [field]: value });
        resetDomainMismatch();
    };

    const handleProofFileChange = (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        setProofFileMeta({
            name: file.name,
            size: file.size,
        });
        uploadProofMutation(file);
    };

    const handleChangeEmail = () => {
        updateForm({ email: "" });
        resetDomainMismatch();
        window.requestAnimationFrame(() => {
            document.getElementById("email")?.focus();
        });
    };

    const handleValidSubmit = () => {
        if (isUploadingProof || isCheckingDomain) {
            return;
        }

        const payload = {
            requester_type: form.requesterType,
            phone: form.phone || null,
            email: form.email || null,
            organization_name: null,
            organization_website: null,
            email_domain_mismatch: false,
            email_domain_proof: null,
        };

        if (isCompanyRequester) {
            payload.organization_name = form.organizationName || null;
            payload.organization_website = form.organizationWebsite || null;
            payload.email_domain_mismatch = emailDomainMismatch;
            payload.email_domain_proof = proofAttachmentId || null;
        }

        onSubmit(payload);
    };

    return (
        <AvForm onValidSubmit={handleValidSubmit}>
            <Row>
                <Col sm="12" className="mb-3">
                    <Label className="form-label d-block mb-2">
                        {t("Submitting as")}
                    </Label>
                    <div className="d-flex flex-wrap gap-4">
                        <FormGroup check inline className="mb-0">
                            <Input
                                type="radio"
                                id="requester_type_company"
                                name="requester_type"
                                checked={isCompanyRequester}
                                onChange={() => handleRequesterTypeChange(REQUESTER_TYPES.COMPANY)}
                            />
                            <Label check htmlFor="requester_type_company" className="ms-1">
                                {t("Company")}
                            </Label>
                        </FormGroup>
                        <FormGroup check inline className="mb-0">
                            <Input
                                type="radio"
                                id="requester_type_private_person"
                                name="requester_type"
                                checked={!isCompanyRequester}
                                onChange={() => handleRequesterTypeChange(REQUESTER_TYPES.PRIVATE_PERSON)}
                            />
                            <Label check htmlFor="requester_type_private_person" className="ms-1">
                                {t("Private person")}
                            </Label>
                        </FormGroup>
                    </div>
                </Col>

                {isCompanyRequester && (
                    <>
                        <Col sm="12" className="mb-3">
                            <Label className="form-label" htmlFor="organization_name">
                                {t("Organization")}
                            </Label>
                            <AvField
                                name="organization_name"
                                type="text"
                                className="form-control"
                                errorMessage={t("This field cannot be blank")}
                                validate={{ required: { value: true } }}
                                id="organization_name"
                                value={form.organizationName}
                                onChange={(e) => updateForm({ organizationName: e.target.value })}
                            />
                        </Col>

                        <Col sm="12" className="mb-3">
                            <Label className="form-label" htmlFor="organization_website">
                                {t("Organization Website")}
                            </Label>
                            <AvField
                                name="organization_website"
                                type="url"
                                className="form-control"
                                errorMessage={t("This field is invalid")}
                                validate={{
                                    required: { value: true },
                                    url: true,
                                }}
                                id="organization_website"
                                value={form.organizationWebsite}
                                onChange={(e) => handleEmailOrWebsiteChange(
                                    "organizationWebsite",
                                    e.target.value
                                )}
                                onBlur={(e) => runDomainCheck({
                                    organizationWebsite: e.target.value,
                                })}
                            />
                        </Col>
                    </>
                )}

                <Col sm="12" className="mb-3">
                    <Label className="form-label" htmlFor="phone">
                        {`${t("Phone Number")} ${t("(Optional)")}`}
                    </Label>
                    <AvField
                        name="phone"
                        type="text"
                        className="form-control"
                        validate={{ required: { value: false } }}
                        id="phone"
                        value={form.phone}
                        onChange={(e) => updateForm({ phone: e.target.value })}
                    />
                </Col>

                <Col sm="12" className="mb-3">
                    <Label className="form-label" htmlFor="email">
                        {t("Email")}
                    </Label>
                    <AvField
                        name="email"
                        type="email"
                        className="form-control"
                        validate={{ required: { value: true }, email: true }}
                        errorMessage={t("This field is invalid")}
                        id="email"
                        value={form.email}
                        onChange={(e) => handleEmailOrWebsiteChange("email", e.target.value)}
                        onBlur={isCompanyRequester
                            ? (e) => runDomainCheck({ email: e.target.value })
                            : undefined
                        }
                    />

                    {isCompanyRequester && emailDomainMismatch && (
                        <DomainMismatchProof
                            t={t}
                            isUploading={isUploadingProof}
                            proofFile={proofFileMeta}
                            fileInputRef={proofFileInputRef}
                            onChangeEmail={handleChangeEmail}
                            onFileChange={handleProofFileChange}
                            onRemoveProof={removeProof}
                        />
                    )}
                </Col>
            </Row>

            <div className="d-flex gap-2 justify-content-end mt-4">
                <Button
                    color="secondary"
                    outline
                    type="button"
                    onClick={onBackButtonClicked}
                >
                    {t("Back")}
                </Button>
                <Button
                    color="primary"
                    type="submit"
                    disabled={isUploadingProof || isCheckingDomain}
                >
                    {t("Next")}
                </Button>
            </div>
        </AvForm>
    );
};

export default withNamespaces()(RequesterDetails);
